











































































































































































































































































































































































import { Component, Watch } from 'vue-property-decorator';
import {
  CUSTOMER_STATUS_LABELS,
  CUSTOMER_TYPE_LABELS,
  CustomerStatus,
  CustomerType,
  ICustomerListItem,
  ICustomerPageStats,
  IUser,
} from 'admiral-core';
import CustomerBetreuerFilter from '@/components/customers/CustomerBetreuerFilter.vue';
import CustomerStatusFilter from '@/components/customers/CustomerStatusFilter.vue';
import CustomerTypeFilter from '@/components/customers/CustomerTypeFilter.vue';
import {
  CheckSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloudDrizzleIcon,
  ListIcon,
  PlusIcon,
  UsersIcon,
  XIcon,
} from 'vue-feather-icons';
import { BaseMixin } from '@/mixins/BaseMixin';
import { mixins } from 'vue-class-component';
import PolicyUserLink from '@/components/common/PolicyUserLink.vue';
import { CustomerApi } from '@/services/CustomerApi';
import CreateCustomerModal from '@/views/pages/customers/CreateCustomerModal.vue';
import OverviewStatisticCard from '@/components/common/OverviewStatisticCard.vue';
import dayjs from 'dayjs';
import { formatCustomerPhones } from '@/utils/formatCustomerAddress';

type CustomerSortColumn = 'name' | 'status' | 'policies' | 'damages' | 'tasks';
type SortOrder = 'asc' | 'desc';

/** Reihenfolge für Status-Sortierung */
const CUSTOMER_STATUS_SORT_ORDER: Record<CustomerStatus, number> = {
  [CustomerStatus.ACTIVE]: 0,
  [CustomerStatus.INACTIVE]: 1,
  [CustomerStatus.ARCHIVED]: 2,
};

@Component({
  name: 'customers',
  components: {
    CreateCustomerModal,
    OverviewStatisticCard,
    CustomerBetreuerFilter,
    CustomerStatusFilter,
    CustomerTypeFilter,
    PlusIcon,
    XIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    UsersIcon,
    ListIcon,
    CloudDrizzleIcon,
    CheckSquareIcon,
    PolicyUserLink,
  },
})
export default class Customers extends mixins(BaseMixin) {
  customers: ICustomerListItem[] = [];
  pageStats: ICustomerPageStats = {
    activePolicies: 0,
    submittedPolicies: 0,
    openDamages: 0,
    newDamages: 0,
    openTasks: 0,
    overdueTasks: 0,
  };
  isLoading = false;
  searchQuery = '';
  statusFilter: CustomerStatus[] = [];
  typeFilter: CustomerType[] = [];
  betreuerFilter: IUser[] = [];
  currentPage = 0;
  itemsPerPage = 20;
  sortValue: { columnName: CustomerSortColumn; order: SortOrder } = {
    columnName: 'name',
    order: 'asc',
  };

  CustomerType = CustomerType;
  CustomerStatus = CustomerStatus;

  /** Gefilterte Kundenliste nach Status, Typ und Suchbegriff. */
  get filteredCustomers(): ICustomerListItem[] {
    let list = this.customers;

    if (this.statusFilter.length > 0) {
      const statusSet = new Set(this.statusFilter);
      list = list.filter((customer) => statusSet.has(customer.status));
    }

    if (this.typeFilter.length > 0) {
      const typeSet = new Set(this.typeFilter);
      list = list.filter((customer) => typeSet.has(customer.type));
    }

    if (this.betreuerFilter.length > 0) {
      const betreuerIds = new Set(this.betreuerFilter.map((user) => user.id));
      list = list.filter((customer) => customer.user && betreuerIds.has(customer.user.id));
    }

    const q = this.searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((customer) => this.customerMatchesSearch(customer, q));
    }

    return list;
  }

  get sortedCustomers(): ICustomerListItem[] {
    const list = [...this.filteredCustomers];
    const { columnName, order } = this.sortValue;
    const direction = order === 'asc' ? 1 : -1;

    list.sort((a, b) => {
      let cmp = 0;
      switch (columnName) {
        case 'name':
          cmp = this.displayName(a)
            .toLocaleLowerCase('de-DE')
            .localeCompare(this.displayName(b).toLocaleLowerCase('de-DE'), 'de-DE');
          break;
        case 'status':
          cmp =
            CUSTOMER_STATUS_SORT_ORDER[a.status] - CUSTOMER_STATUS_SORT_ORDER[b.status];
          break;
        case 'policies':
          cmp = a.policyCount - b.policyCount;
          break;
        case 'damages':
          cmp = a.damageCount - b.damageCount;
          break;
        case 'tasks':
          cmp = a.taskCount - b.taskCount;
          break;
      }
      if (cmp === 0) {
        cmp = this.displayName(a)
          .toLocaleLowerCase('de-DE')
          .localeCompare(this.displayName(b).toLocaleLowerCase('de-DE'), 'de-DE');
      }
      return cmp * direction;
    });
    return list;
  }

  hasSort(columnName: CustomerSortColumn, order: SortOrder): boolean {
    return this.sortValue.columnName === columnName && this.sortValue.order === order;
  }

  /** Spaltensortierung wie in PoliciesOverview (Chevron-Klick). */
  setSort(columnName: CustomerSortColumn, order: SortOrder) {
    this.sortValue = { columnName, order };
    this.currentPage = 0;
  }

  get paginatedCustomers(): ICustomerListItem[] {
    const start = this.currentPage * this.itemsPerPage;
    return this.sortedCustomers.slice(start, start + this.itemsPerPage);
  }

  get pagesCount(): number {
    const count = this.sortedCustomers.length;
    return Math.max(1, Math.ceil(count / this.itemsPerPage) || 1);
  }

  /** Leertext mit aktiven Status-, Typ-, Mitarbeiter- und Suchkriterien. */
  get emptyListMessage(): string {
    const criteria: string[] = [];

    this.statusFilter.forEach((status) => {
      criteria.push(this.statusLabel(status));
    });
    this.typeFilter.forEach((type) => {
      criteria.push(this.typeLabel(type));
    });
    this.betreuerFilter.forEach((user) => {
      const name = [user.lastname, user.firstname].filter(Boolean).join(', ');
      if (name) {
        criteria.push(name);
      }
    });

    const search = this.searchQuery.trim();
    if (search) {
      criteria.push(`„${search}“`);
    }

    if (criteria.length === 0) {
      return 'Noch keine Kunden angelegt.';
    }

    return `Keine Kunden für ${criteria.join(', ')} gefunden.`;
  }

  get paginationRangeLabel(): string {
    const total = this.sortedCustomers.length;
    if (total === 0) {
      return '';
    }
    const start = this.currentPage * this.itemsPerPage + 1;
    const end = Math.min((this.currentPage + 1) * this.itemsPerPage, total);
    return `${start}–${end} von ${total} Kunden`;
  }

  get showDemo(): boolean {
    return this.$store.getters['PreferencesModule/getShowDemo'];
  }

  /** Ob Status-, Typ- oder Suchfilter aktiv sind (KPIs dann aus gefilterter Liste). */
  get hasActiveFilters(): boolean {
    return (
      this.statusFilter.length > 0 ||
      this.typeFilter.length > 0 ||
      this.searchQuery.trim().length > 0
    );
  }

  /** Summiert KPI-Zähler der gefilterten Kunden (gleiche Logik wie Seiten-KPIs). */
  get filteredPageStats(): ICustomerPageStats {
    return this.filteredCustomers.reduce(
      (acc, customer) => {
        const kpi = customer.kpi ?? {
          activePolicies: 0,
          submittedPolicies: 0,
          openDamages: 0,
          newDamages: 0,
          openTasks: 0,
          overdueTasks: 0,
        };
        return {
          activePolicies: acc.activePolicies + kpi.activePolicies,
          submittedPolicies: acc.submittedPolicies + kpi.submittedPolicies,
          openDamages: acc.openDamages + kpi.openDamages,
          newDamages: acc.newDamages + kpi.newDamages,
          openTasks: acc.openTasks + kpi.openTasks,
          overdueTasks: acc.overdueTasks + kpi.overdueTasks,
        };
      },
      {
        activePolicies: 0,
        submittedPolicies: 0,
        openDamages: 0,
        newDamages: 0,
        openTasks: 0,
        overdueTasks: 0,
      },
    );
  }

  /** KPI-Werte: gefiltert aus Kundenliste, sonst Agentur-Gesamt vom Server. */
  get displayStats(): ICustomerPageStats {
    return this.hasActiveFilters ? this.filteredPageStats : this.pageStats;
  }

  get totalCustomers(): number {
    return this.filteredCustomers.length;
  }

  get customersThisMonth(): number {
    return this.filteredCustomers.filter((c) =>
      dayjs(c.createdAt).isSame(dayjs(), 'month'),
    ).length;
  }

  get customersThisMonthSubtitle(): string {
    const count = this.customersThisMonth;
    if (count === 0) {
      return 'Keine neuen Kunden diesen Monat';
    }
    return `+${count} diesen Monat`;
  }

  get activePoliciesCount(): number {
    return this.displayStats.activePolicies;
  }

  get submittedPoliciesCount(): number {
    return this.displayStats.submittedPolicies;
  }

  get submittedPoliciesSubtitle(): string {
    const count = this.submittedPoliciesCount;
    return `${count} ${count === 1 ? 'eingereicht' : 'eingereicht'}`;
  }

  get openDamagesCount(): number {
    return this.displayStats.openDamages;
  }

  get newDamagesCount(): number {
    return this.displayStats.newDamages;
  }

  get newDamagesSubtitle(): string {
    return `${this.newDamagesCount} neu`;
  }

  get openTasksCount(): number {
    return this.displayStats.openTasks;
  }

  get overdueTasksCount(): number {
    return this.displayStats.overdueTasks;
  }

  get overdueTasksSubtitle(): string {
    return `${this.overdueTasksCount} überfällig`;
  }

  async mounted() {
    await this.loadAll();
  }

  /** Lädt Kundenliste mit Zählern und KPIs in einem Request. */
  async loadAll() {
    this.isLoading = true;
    try {
      await this.loadCustomers();
    } finally {
      this.isLoading = false;
    }
  }

  /** Lädt Kunden inkl. Anbahnungs-/Schaden-/Aufgaben-Zähler (ohne volle Listen). */
  async loadCustomers() {
    try {
      const response = await CustomerApi.getCustomers({
        agency: this.agency,
        showDemo: this.showDemo,
      });
      this.customers = response.data.customers;
      this.pageStats = response.data.stats;
      if (this.currentPage > this.pagesCount - 1) {
        this.currentPage = Math.max(0, this.pagesCount - 1);
      }
    } catch (e) {
      console.log(e);
      this.$toastr.e('Kunden konnten nicht geladen werden.');
    }
  }

  /** Wechselt zur angegebenen Seite (0-basiert). */
  goToPage(page: number) {
    if (page < 0 || page >= this.pagesCount) {
      return;
    }
    this.currentPage = page;
  }

  /** Öffnet die Kunden-Detailansicht statt des Bearbeiten-Modals. */
  openCustomerDetail(customer: ICustomerListItem) {
    this.$router.push({ name: 'customers.detail', params: { id: customer.id } }).catch(() => {
      //
    });
  }

  @Watch('searchQuery')
  onSearchQueryChange() {
    this.currentPage = 0;
  }

  onStatusFilterChanged(statuses: CustomerStatus[]) {
    this.statusFilter = [...statuses];
    this.currentPage = 0;
  }

  onTypeFilterChanged(types: CustomerType[]) {
    this.typeFilter = [...types];
    this.currentPage = 0;
  }

  onBetreuerFilterChanged(users: IUser[]) {
    this.betreuerFilter = [...users];
    this.currentPage = 0;
  }

  /** Prüft Name, Kontakt und Status-Labels gegen den Suchbegriff (alle Wörter müssen passen). */
  customerMatchesSearch(customer: ICustomerListItem, query: string): boolean {
    const searchableText = [
      customer.name,
      customer.firstname,
      customer.lastname,
      customer.email,
      customer.phoneLandline,
      customer.phoneMobile,
      customer.street,
      customer.zip,
      customer.city,
      customer.addressSupplement,
      this.typeLabel(customer.type),
      this.statusLabel(customer.status),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const terms = query.split(/\s+/).filter(Boolean);
    return terms.every((term) => searchableText.includes(term));
  }

  displayPhone(customer: ICustomerListItem): string {
    return formatCustomerPhones(customer);
  }

  /** CSS-Klasse für Status-Pill (wie Mockup: weiches Grün/Blau). */
  statusBadgeClass(status: CustomerStatus): string {
    const classes: Record<CustomerStatus, string> = {
      [CustomerStatus.ACTIVE]: 'customer-status-pill--active',
      [CustomerStatus.INACTIVE]: 'customer-status-pill--inactive',
      [CustomerStatus.ARCHIVED]: 'customer-status-pill--archived',
    };
    return classes[status] || 'customer-status-pill--inactive';
  }

  /** Anzeigename des zugewiesenen Mitarbeiters (Mobil-Karten). */
  displayMitarbeiter(customer: ICustomerListItem): string {
    if (!customer.user) {
      return '–';
    }
    return `${customer.user.firstname} ${customer.user.lastname}`.trim() || '–';
  }

  displayName(customer: ICustomerListItem): string {
    if (customer.type === CustomerType.LEGAL) {
      return customer.name || '–';
    }
    const parts = [customer.lastname, customer.firstname].filter(Boolean);
    return parts.length ? parts.join(', ') : '–';
  }

  typeLabel(type: CustomerType): string {
    return CUSTOMER_TYPE_LABELS[type];
  }

  statusLabel(status: CustomerStatus): string {
    return CUSTOMER_STATUS_LABELS[status] || status;
  }
}
